<template>
  <div class="login-page">
    <!-- 左侧：表单区 -->
    <div class="auth-form-section">
      <div class="brand-header">
        <div class="logo-icon">
          <img :src="logoImg" alt="Logo" class="logo-image">
        </div>
        <div class="logo-text">{{ siteTitle }}</div>
      </div>

      <div class="form-wrapper">
        <!-- 登录视图 -->
        <div v-show="currentView === 'login'" class="form-content auth-view" :class="{ active: currentView === 'login' }">
          <div class="welcome-text">
            <div class="welcome-title">{{ $t('common.helloAgain') }} 👋</div>
            <div class="welcome-sub">{{ $t('common.welcome') }}</div>
          </div>

          <!-- 统一登录表单 - 支持电话号码和邮箱 -->
          <el-form :model="loginForm" :rules="rules" ref="loginFormRef" class="form">
            <div class="input-group">
              <label class="input-label">{{ $t('common.accountIdentifier') }}</label>
              <el-form-item prop="account" style="margin-bottom: 0;">
                <el-input
                  v-model="loginForm.account"
                  :placeholder="$t('common.accountIdentifier')"
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <label class="input-label">{{ $t('common.password') }}</label>
                <a class="forgot-link" href="#" @click.prevent="goToForgetPassword">{{ $t('common.forgotPassword') }}</a>
              </div>
              <el-form-item prop="password" style="margin-bottom: 0;">
                <el-input
                  v-model="loginForm.password"
                  type="password"
                  :placeholder="$t('common.password')"
                  show-password
                  class="input-field"
                />
              </el-form-item>
            </div>

            <el-form-item>
              <el-button 
                type="primary" 
                class="btn-submit" 
                @click="onLogin" 
                :loading="loading"
                block
              >
                {{ $t('common.login') }}
              </el-button>
            </el-form-item>
          </el-form>

          <div class="auth-switch">
            {{ $t('common.noAccount') }} <span class="link-highlight" @click="switchView('register')">{{ $t('common.register') }}</span>
          </div>
        </div>

        <!-- 注册视图 -->
        <div v-show="currentView === 'register'" class="form-content auth-view" :class="{ active: currentView === 'register' }">
          <div class="welcome-text">
            <div class="welcome-title">创建账户 🚀</div>
            <div class="welcome-sub">开启您的 AI 自动化之旅</div>
          </div>

          <!-- 手机号短信注册保留为兼容逻辑，当前页面默认使用邮箱注册。 -->
          <el-form v-if="registrationMode === 'phone'" :model="phoneForm" :rules="phoneRules" ref="phoneFormRef" class="form">
            <div class="input-group">
              <label class="input-label">{{ $t('common.phoneNumber') }}</label>
              <el-form-item prop="phone" style="margin-bottom: 0;">
                <el-input
                  v-model="phoneForm.phone"
                  :placeholder="$t('common.phoneNumber')"
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.verificationCode') }}</label>
              <div class="code-row">
                <el-form-item prop="verificationCode" style="margin-bottom: 0; flex: 1;">
                  <el-input
                    v-model="phoneForm.verificationCode"
                    :placeholder="$t('common.verificationCode')"
                    class="input-field"
                  />
                </el-form-item>
                <el-button 
                  type="primary" 
                  class="btn-code"
                  :disabled="!canSendCode || countdown > 0"
                  @click="sendVerificationCode"
                  :loading="sendingCode"
                >
                  {{ countdown > 0 ? `${countdown}${$t('common.countdown')}` : $t('common.sendCode') }}
                </el-button>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.password') }}</label>
              <el-form-item prop="password" style="margin-bottom: 0;">
                <el-input
                  v-model="phoneForm.password"
                  type="password"
                  :placeholder="$t('common.password')"
                  show-password
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.confirmPassword') }}</label>
              <el-form-item prop="confirmPassword" style="margin-bottom: 0;">
                <el-input
                  v-model="phoneForm.confirmPassword"
                  type="password"
                  :placeholder="$t('common.confirmPassword')"
                  show-password
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.inviteCode') }} <span style="font-weight: 400; color: var(--text-secondary);"></span></label>
              <el-form-item prop="inviteCode" style="margin-bottom: 0;">
                <el-input
                  v-model="phoneForm.inviteCode"
                  :placeholder="$t('common.inviteCode')"
                  :disabled="inviteCodeFromUrl"
                  class="input-field"
                />
              </el-form-item>
            </div>

            <el-form-item>
              <el-button 
                type="primary" 
                class="btn-submit" 
                @click="onPhoneRegister" 
                :loading="loading"
                block
              >
                {{ $t('common.register') }}并登录
              </el-button>
            </el-form-item>
          </el-form>

          <!-- 默认邮箱注册，不发送邮件验证码。 -->
          <el-form v-else :model="registerForm" :rules="registerRules" ref="registerFormRef" class="form">
            <div class="input-group">
              <label class="input-label">{{ $t('common.username') }}</label>
              <el-form-item prop="username" style="margin-bottom: 0;">
                <el-input
                  v-model="registerForm.username"
                  :placeholder="$t('common.username')"
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.email') }}</label>
              <el-form-item prop="email" style="margin-bottom: 0;">
                <el-input
                  v-model="registerForm.email"
                  :placeholder="$t('common.email')"
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.password') }}</label>
              <el-form-item prop="password" style="margin-bottom: 0;">
                <el-input
                  v-model="registerForm.password"
                  type="password"
                  :placeholder="$t('common.password')"
                  show-password
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.confirmPassword') }}</label>
              <el-form-item prop="confirmPassword" style="margin-bottom: 0;">
                <el-input
                  v-model="registerForm.confirmPassword"
                  type="password"
                  :placeholder="$t('common.confirmPassword')"
                  show-password
                  class="input-field"
                />
              </el-form-item>
            </div>

            <div class="input-group">
              <label class="input-label">{{ $t('common.inviteCode') }}</label>
              <el-form-item prop="inviteCode" style="margin-bottom: 0;">
                <el-input
                  v-model="registerForm.inviteCode"
                  :placeholder="$t('common.inviteCode')"
                  :disabled="inviteCodeFromUrl"
                  class="input-field"
                >
                  <template #suffix>
                    <InviteCodeHelp />
                  </template>
                </el-input>
              </el-form-item>
            </div>

            <el-form-item>
              <el-button 
                type="primary" 
                class="btn-submit" 
                @click="onRegister" 
                :loading="loading"
                block
              >
                {{ $t('common.register') }}并登录
              </el-button>
            </el-form-item>
          </el-form>

          <div class="auth-switch">
            {{ $t('common.hasAccount') }} <span class="link-highlight" @click="switchView('login')">{{ $t('common.login') }}</span>
          </div>
        </div>
      </div>

      <div class="legal-footer">
        <a href="#" @click.prevent="goToTerms" class="legal-link">{{ $t('legal.termsOfService') }}</a>
        <span class="separator">|</span>
        <a href="#" @click.prevent="goToPrivacy" class="legal-link">{{ $t('legal.privacyPolicy') }}</a>
      </div>
    </div>

    <!-- 右侧：视觉展示区 -->
    <div class="auth-visual-section">
      <div class="bg-logo" :style="{ backgroundImage: `url(${logoImg})` }"></div>
      <div class="bg-pattern"></div>
      
      <div class="glass-card">
        <i class="fa-solid fa-quote-left quote-icon"></i>
        <div class="glass-title">{{ siteTitle }}<br>{{ siteSubtitle }}</div>
        <div class="glass-desc">大象魔法系统，为您提供全流程 AI 赋能，从内容生产到智能创作，让创意无限延伸，让效率成倍提升。</div>
        
        <div class="user-tag">
          <div class="avatar-group">
            <img :src="avatar1" class="avatar" alt="creator">
            <img :src="avatar2" class="avatar" alt="creator">
            <img :src="avatar3" class="avatar" alt="creator">
          </div>
          <div class="tag-text">已有 10,000+ 创作者加入</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/portal/user'
import { ElMessage } from 'element-plus'
import { getAgentInfo, initAgentInfo, setAgentInfoFromUserInfo } from '@/stores/portal/agentStore'
import { getAgentFromStorage } from '@/apps/portal/utils/agentUtils'
import { register, registerByPhone, sendVerificationCode as sendCode } from '@/api/portal/user'

// 导入 Logo
import logoImg from '@/apps/portal/assets/logo.jpg'

// 导入创作者头像
import avatar1 from '@/apps/portal/assets/role/kenjones651.jpg'
import avatar2 from '@/apps/portal/assets/role/thatgirlsydney.jpg'
import avatar3 from '@/apps/portal/assets/role/claire_ai_lifes.jpg'
import InviteCodeHelp from '@/apps/portal/components/InviteCodeHelp.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

// 获取响应式的代理信息
const agentInfo = getAgentInfo()

// 使用 computed 响应式获取标题和副标题
const siteTitle = computed(() => {
  return agentInfo.siteTitle || '大象魔法AIGC'
})

const siteSubtitle = computed(() => {
  return agentInfo.siteSubtitle || '魔法在手，驰骋全球'
})

// 保留手机号注册逻辑，页面默认统一走邮箱注册。
const registrationMode = ref('email')

// 当前视图：'login' 或 'register'
const currentView = ref('login')

// 页面挂载时确保代理信息已加载
onMounted(async () => {
  await initAgentInfo()
  
  // 检查是否有邀请码
  const inviteCode = route.query.inviteCode
  if (inviteCode) {
    phoneForm.inviteCode = inviteCode
    registerForm.inviteCode = inviteCode
    inviteCodeFromUrl.value = true
  }
  
  // 如果 URL 中有 register 参数，切换到注册视图
  if (route.query.register === 'true' || route.name === 'register') {
    currentView.value = 'register'
  }
  
  await nextTick()
})

// 视图切换函数
const switchView = (viewName) => {
  currentView.value = viewName
}

// 统一登录表单
const loginForm = reactive({
  account: '',
  password: '',
  remember: false
})

// 电话号码注册表单
const phoneForm = reactive({
  phone: '',
  verificationCode: '',
  password: '',
  confirmPassword: '',
  inviteCode: '',
})

// 邮箱注册表单
const registerForm = reactive({
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  inviteCode: '',
})

// 验证码相关状态
const countdown = ref(0)
const sendingCode = ref(false)
const canSendCode = computed(() => {
  const phoneRegex = /^1[3-9]\d{9}$/
  return phoneRegex.test(phoneForm.phone)
})

let countdownTimer = null

// 邀请码是否来自URL
const inviteCodeFromUrl = ref(false)


// 统一校验函数 - 支持用户名、电话号码和邮箱
const validateAccount = (rule, value, callback) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phoneRegex = /^1[3-9]\d{9}$/
  const usernameRegex = /^[^\s@]{2,50}$/
  
  if (value === '') {
    callback(new Error(t('validation.accountRequired')))
  } else if (!emailRegex.test(value) && !phoneRegex.test(value) && !usernameRegex.test(value)) {
    callback(new Error(t('validation.accountInvalid')))
  } else {
    callback()
  }
}

const rules = {
  account: [
    { required: true, validator: validateAccount, trigger: 'blur' }
  ],
  password: [
    { required: true, message: t('validation.required'), trigger: 'blur' }
  ]
}

// 注册表单验证规则
const validatePass = (rule, value, callback) => {
  if (value === '') {
    callback(new Error(t('validation.passwordRequired')))
  } else {
    if (registerForm.confirmPassword !== '') {
      registerFormRef.value?.validateField('confirmPassword')
    }
    callback()
  }
}

const validatePass2 = (rule, value, callback) => {
  if (value === '') {
    callback(new Error(t('validation.confirmPasswordRequired')))
  } else if (value !== registerForm.password) {
    callback(new Error(t('validation.passwordMismatch')))
  } else {
    callback()
  }
}

const validateEmail = (rule, value, callback) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (value === '') {
    callback(new Error(t('validation.emailRequired')))
  } else if (!emailRegex.test(value)) {
    callback(new Error(t('validation.emailInvalid')))
  } else {
    callback()
  }
}

const validatePhone = (rule, value, callback) => {
  const phoneRegex = /^1[3-9]\d{9}$/
  if (value === '') {
    callback(new Error(t('validation.phoneRequired')))
  } else if (!phoneRegex.test(value)) {
    callback(new Error(t('validation.phoneInvalid')))
  } else {
    callback()
  }
}

const validateVerificationCode = (rule, value, callback) => {
  const codeRegex = /^\d+$/
  if (value === '') {
    callback(new Error(t('validation.verificationCodeRequired')))
  } else if (!codeRegex.test(value)) {
    callback(new Error(t('validation.verificationCodeInvalid')))
  } else {
    callback()
  }
}

const validatePhonePass = (rule, value, callback) => {
  if (value === '') {
    callback(new Error(t('validation.passwordRequired')))
  } else {
    if (phoneForm.confirmPassword !== '') {
      phoneFormRef.value?.validateField('confirmPassword')
    }
    callback()
  }
}

const validatePhonePass2 = (rule, value, callback) => {
  if (value === '') {
    callback(new Error(t('validation.confirmPasswordRequired')))
  } else if (value !== phoneForm.password) {
    callback(new Error(t('validation.passwordMismatch')))
  } else {
    callback()
  }
}

const phoneRules = {
  phone: [
    { required: true, validator: validatePhone, trigger: 'blur' }
  ],
  verificationCode: [
    { required: true, validator: validateVerificationCode, trigger: 'blur' }
  ],
  password: [
    { required: true, validator: validatePhonePass, trigger: 'blur' },
    { min: 6, message: t('validation.passwordLength'), trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, validator: validatePhonePass2, trigger: 'blur' }
  ],
  inviteCode: []
}

const registerRules = {
  username: [
    { required: true, message: t('validation.usernameRequired'), trigger: 'blur' },
    { min: 2, max: 20, message: t('validation.usernameLength'), trigger: 'blur' }
  ],
  email: [
    { required: true, validator: validateEmail, trigger: 'blur' }
  ],
  password: [
    { required: true, validator: validatePass, trigger: 'blur' },
    { min: 6, message: t('validation.passwordLength'), trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, validator: validatePass2, trigger: 'blur' }
  ],
  inviteCode: [
    { required: true, message: t('validation.inviteCodeRequired'), trigger: 'blur' }
  ]
}

const loginFormRef = ref()
const phoneFormRef = ref()
const registerFormRef = ref()
const loading = ref(false)

const onLogin = async () => {
  if (!loginFormRef.value) return
  
  await loginFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const success = await userStore.login(loginForm.account, loginForm.password)
        if (success) {
          // 登录成功后，从用户信息中提取代理信息并缓存
          if (userStore.userInfo) {
            setAgentInfoFromUserInfo(userStore.userInfo)
          } else {
            // 如果用户信息为空，使用默认信息
            setAgentInfoFromUserInfo(null)
          }
          // 登录成功后跳转到首页
          router.push('/home')
        }
      } catch (error) {
        console.error('Login error:', error)
      } finally {
        loading.value = false
      }
    }
  })
}

// 发送验证码
const sendVerificationCode = async () => {
  if (!canSendCode.value) {
    ElMessage.error(t('validation.phoneInvalid'))
    return
  }

  sendingCode.value = true
  try {
    const response = await sendCode(phoneForm.phone)
    if (response.code === 0) {
      ElMessage.success('验证码发送成功')
      startCountdown()
    } else {
      ElMessage.error(response.msg || '验证码发送失败')
    }
  } catch (error) {
    console.error('Send verification code error:', error)
    ElMessage.error('验证码发送失败')
  } finally {
    sendingCode.value = false
  }
}

// 开始倒计时
const startCountdown = () => {
  countdown.value = 60
  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }, 1000)
}

// 清理倒计时
onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})

// 邮箱注册
const onRegister = async () => {
  if (!registerFormRef.value) return
  
  await registerFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const response = await register({
          username: registerForm.username,
          email: registerForm.email,
          password: registerForm.password,
          confirmPassword: registerForm.confirmPassword,
          inviteCode: registerForm.inviteCode
        })
        
        if (response.code === 0) {
          // 保存token和用户信息
          localStorage.setItem('token', response.token)
          localStorage.setItem('userInfo', JSON.stringify(response.userInfo))
          
          ElMessage.success(t('common.registerSuccess'))
          // 注册成功后跳转到首页
          router.push('/home')
        } else {
          ElMessage.error(response.msg || t('common.registerFailed'))
        }
      } catch (error) {
        console.error('Register error:', error)
        ElMessage.error(t('common.registerFailed'))
      } finally {
        loading.value = false
      }
    }
  })
}

// 手机号注册
const onPhoneRegister = async () => {
  if (!phoneFormRef.value) return
  
  await phoneFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const response = await registerByPhone({
          phone: phoneForm.phone,
          verificationCode: phoneForm.verificationCode,
          password: phoneForm.password,
          confirmPassword: phoneForm.confirmPassword,
          inviteCode: phoneForm.inviteCode
        })
        
        if (response.code === 0) {
          // 保存token和用户信息
          localStorage.setItem('token', response.token)
          localStorage.setItem('userInfo', JSON.stringify(response.userInfo))
          
          ElMessage.success(t('common.registerSuccess'))
          // 注册成功后跳转到首页
          router.push('/home')
        } else {
          ElMessage.error(response.msg || t('common.registerFailed'))
        }
      } catch (error) {
        console.error('Phone register error:', error)
        ElMessage.error(t('common.registerFailed'))
      } finally {
        loading.value = false
      }
    }
  })
}

const goToForgetPassword = () => {
  const agentId = route.query.agent || getAgentFromStorage()
  console.log('goToForgetPassword - agentId:', agentId, 'route.query:', route.query)
  if (agentId) {
    router.push({ path: '/forget-password', query: { agent: agentId } }).catch(err => {
      console.error('跳转到忘记密码页失败:', err)
    })
  } else {
    router.push('/forget-password').catch(err => {
      console.error('跳转到忘记密码页失败:', err)
    })
  }
}

const goToTerms = () => {
  router.push('/terms')
}

const goToPrivacy = () => {
  router.push('/privacy')
}
</script>

<style scoped lang="scss">
@use '@/styles/theme.scss' as *;

.login-page {
  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  font-family: 'Inter', -apple-system, "Microsoft YaHei", sans-serif;
}

/* === 左侧：表单区 === */
.auth-form-section {
  flex: 0 0 480px;
  background: #fff;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10;
  box-shadow: 10px 0 30px rgba(0, 0, 0, 0.02);
}

/* 顶部 Logo */
.brand-header {
  padding: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.logo-text {
  font-size: 20px;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.5px;
}

/* 表单居中容器 */
.form-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 40px;
  position: relative;
}

.form-content {
  width: 100%;
  max-width: 360px;
}

.welcome-text {
  margin-bottom: 32px;
}

.welcome-title {
  font-size: 28px;
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 8px;
  line-height: 1.2;
}

.welcome-sub {
  font-size: 14px;
  color: var(--text-secondary);
}

/* 输入框样式 */
.input-group {
  margin-bottom: 20px;
}

.input-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1F2937; /* 与 login.html 中的 var(--text-main) 一致 */
  margin-bottom: 4px;
}

:deep(.input-field) {
  width: 100%;
  
  .el-input__wrapper {
    padding: 14px 16px !important;
    background: #F8FAFC !important;
    border: 1px solid transparent !important;
    border-radius: 12px !important; /* 与 login.html 中的 var(--radius-md) 一致 */
    box-shadow: none !important;
    transition: all 0.2s;
    min-height: auto !important;
    height: auto !important;
    
    &:hover {
      background: #F1F5F9 !important;
    }
    
    &.is-focus {
      background: #fff !important;
      border-color: var(--accent-blue) !important;
      box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.1) !important;
    }
  }
  
  .el-input__inner {
    height: auto !important;
    line-height: 1.5 !important;
    font-size: 14px !important;
    color: #1F2937 !important; /* 与 login.html 中的 var(--text-main) 一致 */
    padding: 0 !important;
    
    &::placeholder {
      color: #94A3B8 !important; /* 与 login.html 中的 var(--text-placeholder) 一致 */
    }
  }
}

.forgot-link {
  font-size: 12px;
  color: var(--accent-blue);
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
}

/* 提交按钮 */
:deep(.btn-submit) {
  width: 100% !important;
  height: auto !important;
  min-height: auto !important;
  padding: 14px !important;
  background: var(--gradient-brand) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important; /* 与 login.html 中的 var(--radius-md) 一致 */
  font-size: 15px !important;
  font-weight: 600 !important;
  cursor: pointer;
  transition: 0.3s !important; /* 与 login.html 一致 */
  box-shadow: 0 10px 20px -5px rgba(79, 70, 229, 0.4) !important; /* 与 login.html 中的 var(--shadow-btn) 一致 */
  margin-top: 10px;
  
  &:hover:not(.is-loading) {
    opacity: 0.9 !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 15px 25px -5px rgba(79, 70, 229, 0.5) !important;
    background: var(--gradient-brand) !important;
  }
  
  &:active {
    transform: translateY(0) !important;
  }
  
  &.is-loading {
    opacity: 0.8;
  }
}

/* 底部切换链接 */
.auth-switch {
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
}

.link-highlight {
  color: var(--accent-blue);
  font-weight: 600;
  cursor: pointer;
  margin-left: 4px;
  position: relative;
  text-decoration: none;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0%;
    height: 2px;
    background: var(--accent-blue);
    transition: width 0.3s;
  }
  
  &:hover::after {
    width: 100%;
  }
}

/* 底部版权 */
.legal-footer {
  padding: 24px 40px;
  font-size: 12px;
  color: #94A3B8;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.legal-link {
  color: #94A3B8;
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: var(--accent-blue);
  }
}

.separator {
  color: #94A3B8;
}

/* === 右侧：视觉展示区 === */
.auth-visual-section {
  flex: 1;
  background: var(--gradient-brand);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* Logo 底纹 */
.bg-logo {
  position: absolute;
  inset: 0;
  background-size: 1000px 1000px;
  background-repeat: no-repeat;
  opacity: 0.06;
  z-index: 1;
  transform: scale(1.88);
}

/* 背景装饰 */
.bg-pattern {
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 20%),
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 20%);
  opacity: 0.6;
  z-index: 2;
}

/* 磨砂玻璃卡片 */
.glass-card {
  width: 380px;
  height: 280px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-lg);
  padding: 60px;
  color: white;
  box-shadow: var(--shadow-float);
  position: relative;
  z-index: 10;
  transform: translateY(20px);
  animation: floatUp 1s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  
}

@keyframes floatUp {
  from {
    opacity: 0;
    transform: translateY(60px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.quote-icon {
  font-size: 40px;
  opacity: 0.5;
  margin-bottom: 20px;
  display: block;
  line-height: 1;
}

.glass-title {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
}

.glass-desc {
  font-size: 16px;
  line-height: 1.6;
  opacity: 0.8;
  margin-bottom: 40px;
}

/* 用户标签和头像 */
.user-tag {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-group {
  display: flex;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  margin-right: -10px;
  object-fit: cover;
  background: #ddd;
}

.avatar:last-child {
  margin-right: 0;
}

.tag-text {
  font-size: 13px;
  font-weight: 500;
  margin-left: 15px;
  opacity: 0.9;
}

/* 语言切换器 */
.language-switcher-container {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 20;
}

/* 视图切换动画 */
.auth-view {
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.auth-view.active {
  opacity: 1;
  animation: fadeSlide 0.4s ease-out;
}

@keyframes fadeSlide {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* 验证码组合 */
.code-row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

:deep(.btn-code) {
  padding: 14px 16px !important;
  background: #EEF2FF !important;
  color: var(--accent-blue) !important;
  border: 1px solid rgba(79, 70, 229, 0.1) !important;
  border-radius: 12px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  white-space: nowrap;
  transition: 0.2s;
  height: auto !important;
  min-height: auto !important;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none !important;
  line-height: 1 !important;
  
  .el-button__inner {
    line-height: 1 !important;
  }
  
  &:hover:not(:disabled):not(.is-loading) {
    background: #E0E7FF !important;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #EEF2FF !important;
  }
  
  &.is-loading {
    opacity: 0.8;
  }
}

/* 响应式 */
@media (max-width: 1024px) {
  .auth-form-section {
    flex: 0 0 400px;
  }
}

@media (max-width: 768px) {
  .login-page {
    flex-direction: column;
  }
  
  .auth-visual-section {
    display: none;
  }
  
  .auth-form-section {
    flex: 1;
    width: 100%;
    box-shadow: none;
  }
  
  .form-wrapper {
    padding: 20px;
    align-items: flex-start;
  }
  
  .brand-header {
    padding: 20px;
  }
  
  .legal-footer {
    padding: 20px;
  }
}
</style>
